import { useMemo } from 'react';
import { Plus } from 'lucide-react';
import type {
  RoutingSkuTargetRecord,
  RoutingTimeWindowRecord,
} from '@nadoutong/contracts';
import { useT } from '../../i18n/index.ts';
import { Button, Chip } from '../../ui/index.ts';
import {
  dayTimelineSegments,
  formatMinuteOfDay,
  weekdayIndexes,
  windowsForSku,
  TIMELINE_MINUTES,
  type TimelineSegment,
} from './routingDayTimeline.ts';
import { joinIdentitySegments, targetIdentitySegments } from '../tenantRoutingShared.ts';

/**
 * The selected SKU's day, as a 24-hour band (0044).
 *
 * Design brief, verbatim: an operator must be able to answer "who serves this
 * right now" and "who serves it at 03:00" without doing arithmetic. So the
 * band is segmented by schedule, every segment spells out its legs and their
 * weights, the segment containing this moment is marked, and the uncovered
 * stretch is labelled as the standard pool rather than left blank — a blank
 * stretch is exactly the "no route at night" misreading this feature is built
 * to be incapable of.
 *
 * Colour follows `TenantRoutingPoolShareBar`: the Deck palette has no
 * categorical hues and the three lamp colours mean health, so scheduled
 * stretches are `signal` and the standard stretch is the neutral rail.
 * Identity comes from the legend, not from colour.
 */

interface Props {
  readonly skuId: string;
  readonly targets: readonly RoutingSkuTargetRecord[];
  /** Every window of the enclosing config — filtered to this SKU internally. */
  readonly windows: readonly RoutingTimeWindowRecord[];
  /** False while the window read is in flight or failed; the band is hidden. */
  readonly windowsLoaded: boolean;
  /** Injected in tests; defaults to the render instant. */
  readonly now?: Date;
  readonly onAdd: () => void;
  readonly onEdit: (window: RoutingTimeWindowRecord) => void;
}

function legendLabel(
  targets: readonly RoutingSkuTargetRecord[],
  targetId: string,
): string {
  const target = targets.find((candidate) => candidate.id === targetId);
  if (target === undefined) return targetId;
  return target.upstream_channel_slug.length > 0
    ? target.upstream_channel_slug
    : joinIdentitySegments(targetIdentitySegments(target, target.upstream_channel_slug).slice(0, 1));
}

function segmentPercent(segment: TimelineSegment): number {
  return ((segment.endMinute - segment.startMinute) / TIMELINE_MINUTES) * 100;
}

export function TenantRoutingTimeWindows({
  skuId,
  targets,
  windows,
  windowsLoaded,
  now,
  onAdd,
  onEdit,
}: Props): JSX.Element {
  const t = useT();
  const instant = useMemo(() => now ?? new Date(), [now]);
  const applicable = useMemo(() => windowsForSku(windows, targets), [windows, targets]);
  const segments = useMemo(
    () => dayTimelineSegments({ targets, windows, now: instant }),
    [targets, windows, instant],
  );
  const currentSegment = segments.find((segment) => segment.current) ?? null;
  const weekdayLabels = [
    t('tenant_routing.time_windows.day_mon'),
    t('tenant_routing.time_windows.day_tue'),
    t('tenant_routing.time_windows.day_wed'),
    t('tenant_routing.time_windows.day_thu'),
    t('tenant_routing.time_windows.day_fri'),
    t('tenant_routing.time_windows.day_sat'),
    t('tenant_routing.time_windows.day_sun'),
  ];

  return (
    <div
      className="flex flex-col gap-2 border-t border-line-soft/50 px-4 pb-3 pt-2.5"
      data-testid={`tenant-routing-time-windows-${skuId}`}
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <span className="text-label text-paper-mid">{t('tenant_routing.time_windows.title')}</span>
          {/* The answer to "who serves this right now", stated rather than
              left to be read off the band. */}
          <span
            className="text-micro text-paper-low"
            data-testid={`tenant-routing-time-windows-now-${skuId}`}
          >
            {t('tenant_routing.time_windows.now', {
              time: formatMinuteOfDay(instant.getHours() * 60 + instant.getMinutes()),
              schedule: currentSegment?.window?.label
                ?? t('tenant_routing.time_windows.standard'),
            })}
          </span>
        </div>
        <span className="inline-flex" title={windowsLoaded ? undefined : t('tenant_routing.time_windows.write_blocked')}>
          <Button
            size="sm"
            variant="ghost"
            disabled={!windowsLoaded}
            onClick={onAdd}
            data-testid={`tenant-routing-time-windows-add-${skuId}`}
          >
            <Plus aria-hidden="true" className="h-3.5 w-3.5" />
            {t('tenant_routing.time_windows.add')}
          </Button>
        </span>
      </div>

      {!windowsLoaded ? (
        <p
          className="m-0 text-micro text-paper-low"
          data-testid={`tenant-routing-time-windows-unloaded-${skuId}`}
        >
          {t('tenant_routing.time_windows.unloaded')}
        </p>
      ) : applicable.length === 0 ? (
        <p
          className="m-0 text-micro text-paper-low"
          data-testid={`tenant-routing-time-windows-none-${skuId}`}
        >
          {t('tenant_routing.time_windows.none')}
        </p>
      ) : (
        <>
          {/* Decoration; the rows below are the readable copy (DESIGN.md §9). */}
          <div className="flex h-2 gap-px overflow-hidden rounded-full bg-line-soft" aria-hidden="true">
            {segments.map((segment) => (
              <div
                key={`${segment.startMinute}`}
                className={segment.window === null
                  ? 'h-full flex-none bg-line first:rounded-l-full last:rounded-r-full'
                  : 'h-full flex-none bg-signal first:rounded-l-full last:rounded-r-full'}
                style={{ width: `${String(segmentPercent(segment))}%` }}
              />
            ))}
          </div>
          <div className="flex justify-between font-mono text-micro text-paper-low" aria-hidden="true">
            <span>00</span>
            <span>06</span>
            <span>12</span>
            <span>18</span>
            <span>24</span>
          </div>

          <ul className="m-0 flex list-none flex-col gap-1 p-0">
            {segments.map((segment) => (
              <li
                key={`${segment.startMinute}`}
                className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-micro"
                data-testid={`tenant-routing-time-window-segment-${skuId}-${segment.startMinute}`}
              >
                <span className="font-mono text-paper-mid">
                  {formatMinuteOfDay(segment.startMinute)}–{formatMinuteOfDay(segment.endMinute)}
                </span>
                <span className="text-paper">
                  {segment.window?.label ?? t('tenant_routing.time_windows.standard')}
                </span>
                {segment.current ? (
                  <Chip
                    tone="signal"
                    lamp={false}
                    testId={`tenant-routing-time-window-current-${skuId}`}
                  >
                    {t('tenant_routing.time_windows.in_force')}
                  </Chip>
                ) : null}
                <span className="min-w-0 font-mono text-paper-low">
                  {segment.legs.length === 0
                    ? t('tenant_routing.time_windows.no_legs')
                    : segment.legs
                        .map((leg) => `${legendLabel(targets, leg.targetId)} ${String(leg.weight)}`)
                        .join(' · ')}
                </span>
              </li>
            ))}
          </ul>

          {/* The stated fallback. Load-bearing copy: without it a reader can
              take an unlabelled stretch for "nothing serves this then". */}
          <p
            className="m-0 text-micro text-paper-low"
            data-testid={`tenant-routing-time-windows-fallback-${skuId}`}
          >
            {t('tenant_routing.time_windows.fallback_note')}
          </p>

          <ul className="m-0 flex list-none flex-col gap-0.5 p-0">
            {applicable.map((window) => (
              <li
                key={window.id}
                className="flex flex-wrap items-baseline gap-x-2 text-micro"
                data-testid={`tenant-routing-time-window-row-${window.id}`}
              >
                <button
                  type="button"
                  className="text-paper underline decoration-dotted underline-offset-2"
                  onClick={() => onEdit(window)}
                  data-testid={`tenant-routing-time-window-edit-${window.id}`}
                >
                  {window.label}
                </button>
                <span className="font-mono text-paper-mid">
                  {formatMinuteOfDay(window.start_minute)}–{formatMinuteOfDay(window.end_minute)}
                </span>
                <span className="text-paper-low">{window.timezone}</span>
                <span className="text-paper-low">
                  {weekdayIndexes(window.days_of_week)
                    .map((day) => weekdayLabels[day] ?? '')
                    .join('')}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
